import axios from 'axios';
import { useEffect, useState } from 'react';
import { Modal, ScrollView, StyleSheet, View } from 'react-native';
import { Button } from '../../../components/primitives/Button';
import { DatePickerInput } from '../../../components/primitives/DatePickerInput';
import { FormInput } from '../../../components/primitives/FormInput';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { TimePickerInput } from '../../../components/primitives/TimePickerInput';
import { useModal } from '../../../contexts/ModalContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { CANCELLABLE_EVENT_STATUSES, type CalendarEvent } from '../../../types/calendar';
import {
  useCancelCalendarEvent,
  useCreateCalendarEvent,
  useUpdateCalendarEvent,
} from '../hooks/useCalendarMutations';
import { type LeadInviteProtectionStatus, LeadInviteToggle } from './LeadInviteToggle';
import { OpenLeadLink } from './OpenLeadLink';

interface EventFormProps {
  organizationId: string;
  visible: boolean;
  onClose: () => void;
  onSaved?: (event: CalendarEvent) => void;
  /** Present = editing that event; absent = creating a new one. */
  event?: CalendarEvent;
  /** Organization.leadInviteEnabled === true — see useOrgSettings. */
  orgInviteEnabled: boolean;
  /** null when no lead is attached, or while that lead's status is still loading. */
  leadProtection: LeadInviteProtectionStatus | null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatLocalTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

// The form's only timezone conversion: date/time strings are always entered
// and displayed in the VIEWER's local wall-clock (device zone) — `Date`'s
// local getters/constructor already do this for free — and only converted to
// a UTC instant at the submission boundary via toISOString(). This is layer
// 3 (display) and layer 1 (storage) of design Q3's three-layer model; layer 2
// (interpretation) is the explicit `timeZone` field sent alongside them.
function combineLocalDateTime(dateStr: string, timeStr: string): Date | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeStr);
  if (!dateMatch || !timeMatch) return null;
  const [, y, mo, d] = dateMatch;
  const [, hh, mm] = timeMatch;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm), 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    if (Array.isArray(data?.message)) return data.message.join(' ');
    if (typeof data?.message === 'string') return data.message;
  }
  return error instanceof Error ? error.message : fallback;
}

// Found incidentally while fixing lint issues, unrelated to them: opening
// "New event" inside the last hour before local midnight used to default
// `endTime` to a wrapped-around early-morning clock time (e.g. 23:21 -> a
// 00:21 endTime) while `date` stayed on today — end reads as earlier than
// start on the single `date` this form uses, making the default state
// itself invalid until the rep manually fixed the date or times. Capping to
// the same calendar day's last minute keeps the default valid; the only
// case this doesn't cover is the literal last minute of the day, where no
// same-day default can be both >0 minutes long and after `now`.
export function defaultEndTime(now: Date): Date {
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 0);
  return new Date(Math.min(now.getTime() + 60 * 60_000, endOfDay.getTime()));
}

function initialFieldsFrom(event: CalendarEvent | undefined) {
  if (!event) {
    const now = new Date();
    const inOneHour = defaultEndTime(now);
    return {
      title: '',
      description: '',
      location: '',
      date: formatLocalDate(now),
      startTime: formatLocalTime(now),
      endTime: formatLocalTime(inOneHour),
      leadEmail: '',
      leadInviteRequested: false,
    };
  }
  const start = new Date(event.startsAt);
  const end = new Date(event.endsAt);
  return {
    title: event.title,
    description: event.description ?? '',
    location: event.location ?? '',
    date: formatLocalDate(start),
    startTime: formatLocalTime(start),
    endTime: formatLocalTime(end),
    leadEmail: event.leadEmail ?? '',
    leadInviteRequested: event.leadInviteRequested,
  };
}

/**
 * The pure form content, rendered without a `Modal` wrapper so it can be
 * exercised directly in tests — react-test-renderer cannot mount a
 * react-native `Modal` while it is open (see CreateExperimentModal.tsx for
 * the established pattern in this repo). Only `EventFormModal` (below) wraps
 * this in the actual `Modal`.
 */
export function EventForm({
  organizationId,
  visible,
  onClose,
  onSaved,
  event,
  orgInviteEnabled,
  leadProtection,
}: Readonly<EventFormProps>) {
  const isEditMode = !!event;
  const create = useCreateCalendarEvent();
  const updateMutation = useUpdateCalendarEvent();
  const cancelMutation = useCancelCalendarEvent();
  const activeMutation = isEditMode ? updateMutation : create;
  const { confirm } = useModal();

  const canCancelMeeting = isEditMode && CANCELLABLE_EVENT_STATUSES.has(event.status);

  const [fields, setFields] = useState(() => initialFieldsFrom(event));

  // Deliberately keyed on event?.id, not `event` itself: a query refetch can
  // hand back a fresh object for the same event, and re-seeding on every such
  // refresh would silently overwrite whatever the user is mid-typing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: event?.id, not event, is intentional — see above
  useEffect(() => {
    if (visible) {
      setFields(initialFieldsFrom(event));
    }
  }, [visible, event?.id]);

  const start = combineLocalDateTime(fields.date, fields.startTime);
  const end = combineLocalDateTime(fields.date, fields.endTime);
  const isTimeRangeValid = !!start && !!end && end.getTime() > start.getTime();
  const isEmailValid =
    fields.leadEmail.trim() === '' || EMAIL_PATTERN.test(fields.leadEmail.trim());
  const isValid = fields.title.trim().length > 0 && isTimeRangeValid && isEmailValid;

  function updateField<K extends keyof typeof fields>(key: K, value: (typeof fields)[K]) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit() {
    if (!isValid || !start || !end) return;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const shared = {
      title: fields.title.trim(),
      description: fields.description.trim() || undefined,
      location: fields.location.trim() || undefined,
      startsAt: start.toISOString(),
      endsAt: end.toISOString(),
      timeZone,
      leadInviteRequested: fields.leadInviteRequested,
    };

    if (isEditMode) {
      updateMutation.mutate(
        {
          id: event.id,
          organizationId,
          payload: { ...shared, leadEmail: fields.leadEmail.trim() || null },
        },
        {
          onSuccess: (saved) => {
            onSaved?.(saved);
            onClose();
          },
        },
      );
    } else {
      create.mutate(
        { ...shared, organizationId, leadEmail: fields.leadEmail.trim() || undefined },
        {
          onSuccess: (saved) => {
            onSaved?.(saved);
            onClose();
          },
        },
      );
    }
  }

  // Destructive and, once Google sync is enabled for an org, capable of
  // sending a cancellation notice to a lead — never a single misplaced tap.
  // Uses the house global confirm() (ModalContext) rather than rendering a
  // ConfirmModal/Sheet inline, matching ConsentSection.tsx's revoke flow.
  async function handleCancelMeeting() {
    if (!event) return;
    const confirmed = await confirm({
      title: 'Cancel this meeting?',
      message: `This cancels "${event.title}" for everyone invited. This can't be undone.`,
      confirmLabel: 'Cancel meeting',
      cancelLabel: 'Keep meeting',
      variant: 'destructive',
    });
    if (!confirmed) return;

    cancelMutation.mutate(
      { id: event.id, organizationId },
      {
        onSuccess: (cancelled) => {
          onSaved?.(cancelled);
          onClose();
        },
      },
    );
  }

  if (!visible) return null;

  const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const showEventZoneLabel = isEditMode && event.timeZone !== viewerZone;

  return (
    <View style={styles.overlay}>
      <View
        style={styles.card}
        accessible
        accessibilityLabel={isEditMode ? 'Edit event' : 'New event'}
      >
        <Text variant="h5" color={colors.text.primary} accessibilityRole="header">
          {isEditMode ? 'Edit event' : 'New event'}
        </Text>
        {isEditMode && (
          <OpenLeadLink leadId={event.leadId} testID="event_form_open_lead" onNavigate={onClose} />
        )}

        <ScrollView
          testID="event_form_scroll"
          style={styles.scroll}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator
        >
          <VStack spacing="md" style={{ marginTop: tokens.spacing.md }}>
            <VStack spacing="xs">
              <Text variant="label" color={colors.text.secondary}>
                Title
              </Text>
              <FormInput
                testID="event_form_title"
                value={fields.title}
                onChangeText={(v) => updateField('title', v)}
                placeholder="Discovery call"
                style={styles.input}
                accessibilityLabel="Event title"
              />
            </VStack>

            <VStack spacing="xs">
              <Text variant="label" color={colors.text.secondary}>
                Description
              </Text>
              <FormInput
                testID="event_form_description"
                value={fields.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder="Optional"
                style={[styles.input, styles.descriptionInput]}
                multiline
                numberOfLines={6}
                accessibilityLabel="Event description"
              />
            </VStack>

            <VStack spacing="xs">
              <Text variant="label" color={colors.text.secondary}>
                Location
              </Text>
              <FormInput
                testID="event_form_location"
                value={fields.location}
                onChangeText={(v) => updateField('location', v)}
                placeholder="Optional"
                style={styles.input}
                accessibilityLabel="Event location"
              />
            </VStack>

            <VStack spacing="xs">
              <Text variant="label" color={colors.text.secondary}>
                Date
              </Text>
              <DatePickerInput
                testID="event_form_date"
                value={fields.date}
                onChange={(v) => updateField('date', v)}
                accessibilityLabel="Event date"
              />
            </VStack>

            <HStack spacing="md">
              <VStack spacing="xs" style={{ flex: 1 }}>
                <Text variant="label" color={colors.text.secondary}>
                  Start time
                </Text>
                <TimePickerInput
                  testID="event_form_start_time"
                  value={fields.startTime}
                  onChange={(v) => updateField('startTime', v)}
                  accessibilityLabel="Event start time"
                />
              </VStack>
              <VStack spacing="xs" style={{ flex: 1 }}>
                <Text variant="label" color={colors.text.secondary}>
                  End time
                </Text>
                <TimePickerInput
                  testID="event_form_end_time"
                  value={fields.endTime}
                  onChange={(v) => updateField('endTime', v)}
                  accessibilityLabel="Event end time"
                />
              </VStack>
            </HStack>

            {!isTimeRangeValid && (
              <Text
                testID="event_form_time_range_error"
                variant="caption"
                color={colors.status.error.main}
                accessibilityRole="alert"
              >
                End time must be after the start time.
              </Text>
            )}

            {showEventZoneLabel && (
              <Text variant="caption" color={colors.text.tertiary}>
                Event zone: {event.timeZone} (you are viewing in {viewerZone})
              </Text>
            )}

            <VStack spacing="xs">
              <Text variant="label" color={colors.text.secondary}>
                Lead email
              </Text>
              <FormInput
                testID="event_form_lead_email"
                value={fields.leadEmail}
                onChangeText={(v) => updateField('leadEmail', v)}
                placeholder="Optional"
                keyboardType="email-address"
                autoCapitalize="none"
                style={styles.input}
                accessibilityLabel="Lead email"
              />
              {!isEmailValid && (
                <Text
                  testID="event_form_lead_email_error"
                  variant="caption"
                  color={colors.status.error.main}
                  accessibilityRole="alert"
                >
                  Enter a valid email address.
                </Text>
              )}
            </VStack>

            <LeadInviteToggle
              orgInviteEnabled={orgInviteEnabled}
              leadProtection={leadProtection}
              checked={fields.leadInviteRequested}
              onToggle={(next) => updateField('leadInviteRequested', next)}
            />
          </VStack>

          {activeMutation.isError && (
            <Text
              testID="event_form_server_error"
              variant="caption"
              color={colors.status.error.main}
              accessibilityRole="alert"
              accessibilityLiveRegion="assertive"
              style={{ marginTop: tokens.spacing.sm }}
            >
              {extractErrorMessage(activeMutation.error, 'Could not save this event.')}
            </Text>
          )}

          {cancelMutation.isError && (
            <Text
              testID="event_form_cancel_error"
              variant="caption"
              color={colors.status.error.main}
              accessibilityRole="alert"
              accessibilityLiveRegion="assertive"
              style={{ marginTop: tokens.spacing.sm }}
            >
              {extractErrorMessage(cancelMutation.error, 'Could not cancel this meeting.')}
            </Text>
          )}
        </ScrollView>

        <HStack spacing="md" style={styles.footer} justify="between">
          {/* The destructive action lives apart from Close/Save, on its own
              side of the footer — a fat-finger next to Save must not cancel
              a customer meeting. */}
          {canCancelMeeting ? (
            <Button
              variant="outline"
              size="sm"
              onPress={handleCancelMeeting}
              loading={cancelMutation.isPending}
              disabled={activeMutation.isPending || cancelMutation.isPending}
              textColor={colors.status.error.main}
              accessibilityLabel={`Cancel ${event.title}`}
            >
              Cancel meeting
            </Button>
          ) : (
            <View />
          )}

          <HStack spacing="md">
            {/* Renamed from "Cancel" (Unit 6): this dismisses the form
                without saving — a different action from "Cancel meeting"
                above, which cancels the customer's meeting. Two controls
                both saying Cancel was a genuine footgun. */}
            <Button
              variant="outline"
              size="sm"
              onPress={onClose}
              disabled={activeMutation.isPending || cancelMutation.isPending}
              accessibilityLabel="Close"
            >
              Close
            </Button>
            <Button
              variant="primary"
              size="sm"
              onPress={handleSubmit}
              loading={activeMutation.isPending}
              disabled={!isValid || activeMutation.isPending || cancelMutation.isPending}
              accessibilityLabel={isEditMode ? 'Save changes' : 'Create event'}
            >
              Save
            </Button>
          </HStack>
        </HStack>
      </View>
    </View>
  );
}

type EventFormModalProps = EventFormProps;

/**
 * Wraps `EventForm` in the actual react-native `Modal` for runtime use. Not
 * exercised directly in tests — see `EventForm`.
 */
export function EventFormModal(props: Readonly<EventFormModalProps>) {
  return (
    <Modal visible={props.visible} transparent animationType="fade" onRequestClose={props.onClose}>
      <EventForm {...props} />
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    maxHeight: '90%',
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: tokens.spacing.lg,
  },
  input: {
    backgroundColor: colors.background.input,
    borderWidth: 1,
    borderColor: colors.border.input,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    minHeight: 44,
  },
  scroll: {
    flexShrink: 1,
  },
  descriptionInput: {
    minHeight: 132,
    textAlignVertical: 'top',
  },
  footer: {
    marginTop: tokens.spacing.lgPlus,
    justifyContent: 'flex-end',
  },
});
